import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import accountService from '../services/accountService';
import CountrySelect from './CountrySelect';
import CurrencySelect from './CurrencySelect';
import ConfirmModal from './shared/ConfirmModal';
import NotificationsPanel from './shared/NotificationsPanel';
import './SchedulingTab.css';

// CHAIN SETTINGS › SCHEDULING (V596, V597) — shown when the chain has Enforce Scheduling on.
//
// THE BOOKING COMES FIRST. The chain's admins propose a cutover date for a property on an order form; the chain's
// partner commits to it or declines it, saying why; the sync project is then created FROM the committed booking
// (Create sync project → the normal wizard, prefilled, cutover = the committed date). No date may be sooner than
// the chain's lead time. The chart is the year, January to December: one row per booking, a 2-day block at its date.
// The server decides who may do what (accounts/schedule.js); canPropose / canCommit only choose the buttons.

const DAY = 86400000;
const COL = 26;
const parse = (s) => (s ? new Date(`${s}T00:00:00Z`) : null);
const ymd = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => new Date(d.getTime() + n * DAY);
const todayUtc = () => parse(ymd(new Date()));
const fmt = (s) => (s ? parse(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—');
const isWeekend = (d) => d.getUTCDay() === 0 || d.getUTCDay() === 6;

const STATUS_LABEL = { PROPOSED: 'Proposed', COMMITTED: 'Committed', DECLINED: 'Declined', CANCELLED: 'Cancelled' };
const EMPTY_FORM = {
  hotelName: '', hotelCode: '', country: '', currency: '', proposedDate: '',
  contactName: '', contactEmail: '', contactPhone: '', contactRole: '', notes: '',
};

const SchedulingTab = ({ accountId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [form, setForm] = useState(null);          // the order form, open: { ...fields, requestId? }
  const [saving, setSaving] = useState(false);
  const [decision, setDecision] = useState(null);  // { kind: 'commit'|'decline'|'cancel', request, date, reason }
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const scrollRef = useRef(null);
  // Who is told of booking changes — the shared notifications editor, saved as it is changed.
  const [notif, setNotif] = useState({ emails: [], teamsWebhook: null });
  const [notifOpen, setNotifOpen] = useState(false);
  useEffect(() => {
    accountService.getScheduleNotifications(accountId)
      .then((n) => setNotif({ emails: n?.emails || [], teamsWebhook: n?.teamsWebhook || null }))
      .catch(() => {});
  }, [accountId]);
  const notifPersistence = {
    addEmail:    ({ email, source }) => accountService.addScheduleNotificationEmail(accountId, { email, source }),
    removeEmail: (id)                => accountService.removeScheduleNotificationEmail(accountId, id),
    saveWebhook: (url)               => accountService.saveScheduleNotificationWebhook(accountId, url),
  };
  // A date inside the lead time is refused AS IT IS ENTERED. min="" only limits the calendar picker; the arrow keys
  // and typing step straight past it. So an earlier date snaps back to the earliest allowed, and says so.
  const [dateNote, setDateNote] = useState(null);
  const notEarlier = (value) => {
    if (!value || !data || value >= data.earliestDate) { setDateNote(null); return value; }
    setDateNote(`${fmt(value)} is inside the ${data.leadTimeDays}-day lead time — the earliest is ${fmt(data.earliestDate)}.`);
    return data.earliestDate;
  };
  // The booking just saved: the chart moves to its date and it flashes, so the change is seen where it landed.
  const [focus, setFocus] = useState(null);       // { id, date, at }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setData(await accountService.getSchedule(accountId));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the schedule');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  // The year, 1 January to 31 December.
  const days = useMemo(() => {
    const out = [];
    for (let d = new Date(Date.UTC(year, 0, 1)); d.getUTCFullYear() === year; d = addDays(d, 1)) out.push(d);
    return out;
  }, [year]);
  const yearStart = days[0];
  const today = todayUtc();
  const earliest = data ? parse(data.earliestDate) : today;

  // One row per booking that is still in play; a cancelled one is in the list below, not on the chart.
  const rows = useMemo(() => (data ? data.requests.filter((r) => r.status !== 'CANCELLED') : []), [data]);

  // Where the chart opens: on the booking just saved (centred), else on today when this year is shown.
  useEffect(() => {
    if (!scrollRef.current || !data) return;
    const el = scrollRef.current;
    const focusDate = focus && parse(focus.date);
    if (focusDate && focusDate.getUTCFullYear() === year) {
      const x = Math.round((focusDate - yearStart) / DAY) * COL;
      el.scrollTo({ left: Math.max(0, x - el.clientWidth / 2 + COL), behavior: 'smooth' });
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    const target = year === today.getUTCFullYear() ? Math.max(0, Math.round((today - yearStart) / DAY) - 7) : 0;
    el.scrollLeft = target * COL;
  }, [year, data, focus]); // eslint-disable-line

  // The flash wears off; the next save starts a new one.
  useEffect(() => {
    if (!focus) return undefined;
    const t = setTimeout(() => setFocus(null), 4500);
    return () => clearTimeout(t);
  }, [focus]);

  // After a save: reload, then show the booking where it now is — its year, its date, flashing.
  const showSaved = async (request) => {
    await load();
    const date = request && (request.committedDate || request.proposedDate);
    if (!request || !date) return;
    setYear(parse(date).getUTCFullYear());
    setFocus({ id: request.id, date, at: Date.now() });
  };

  const openForm = (request = null) => {
    setForm(request
      ? { ...EMPTY_FORM, ...Object.fromEntries(Object.keys(EMPTY_FORM).map((k) => [k, request[k] ?? ''])), requestId: request.id }
      : { ...EMPTY_FORM, proposedDate: data?.earliestDate || '' });
    setNotice(null);
    setError(null);
    setDateNote(null);
  };

  // A contact given before fills the access-contact fields (still editable). Matched by email address.
  const pickContact = (email) => {
    const c = (data.contacts || []).find((x) => x.contactEmail === email);
    if (!c) return;
    setForm((f) => ({ ...f, contactName: c.contactName || '', contactRole: c.contactRole || '',
      contactEmail: c.contactEmail || '', contactPhone: c.contactPhone || '' }));
  };

  const setField = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const formBody = () => {
    const { requestId, ...fields } = form;
    return fields;
  };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      let saved;
      if (form.requestId) {
        saved = await accountService.actOnSchedule(accountId, form.requestId, { action: 'update', ...formBody() });
        setNotice('Booking updated — the partner will need to commit to it again.');
      } else {
        saved = await accountService.proposeSchedule(accountId, formBody());
        setNotice('Date proposed — the partner will commit to it or say why not.');
      }
      setForm(null);
      await showSaved(saved?.request);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the booking');
    } finally {
      setSaving(false);
    }
  };

  const decide = async () => {
    const { kind, request, date, reason } = decision;
    try {
      setSaving(true);
      setError(null);
      const saved = await accountService.actOnSchedule(accountId, request.id, {
        action: kind, ...(kind === 'commit' ? { committedDate: date } : {}), ...(kind === 'decline' ? { reason } : {}),
      });
      setNotice(kind === 'commit' ? `Committed for ${fmt(date)} — the sync project can now be created from it.`
        : kind === 'decline' ? 'Declined — the chain can propose another date.' : 'Cancelled.');
      setDecision(null);
      await showSaved(saved?.request);
    } catch (err) {
      setError(err.response?.data?.message || `Could not ${kind}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <div className="tab-content"><p>Loading schedule…</p></div>;
  if (!data) return <div className="tab-content"><div className="sched-error">{error}</div></div>;

  const offset = (d) => Math.round((d - yearStart) / DAY);
  const inYear = (s) => s && parse(s).getUTCFullYear() === year;
  const dateOf = (r) => r.committedDate || r.proposedDate;
  const createProject = (r) => navigate(`/sync/create?scheduleRequest=${r.id}`);

  return (
    <div className="tab-content scheduling">
      <div className="sched-head">
        <div>
          <h3>Scheduling</h3>
          <p className="sched-sub">
            Book each property's migration. Dates must be at least <b>{data.leadTimeDays}</b> day(s) away —
            the earliest is <b>{fmt(data.earliestDate)}</b>. Once the partner commits, create the sync project from the booking.
          </p>
        </div>
        {data.canPropose && (
          <button type="button" className="btn btn-primary" onClick={() => openForm()}>Propose a date</button>
        )}
      </div>

      {notice && <div className="sched-notice">{notice}</div>}
      {error && !form && <div className="sched-error">{error}</div>}

      {/* ── the year ────────────────────────────────────────────────────────────────────────────────────────── */}
      <div className="gantt-year">
        <button type="button" className="btn btn-sm btn-secondary" onClick={() => setYear((y) => y - 1)}>‹ {year - 1}</button>
        <strong>{year}</strong>
        <button type="button" className="btn btn-sm btn-secondary" onClick={() => setYear((y) => y + 1)}>{year + 1} ›</button>
      </div>
      <div className="gantt">
        <div className="gantt-labels">
          <div className="gantt-label gantt-corner">Property</div>
          {rows.length === 0 && <div className="gantt-label gantt-empty">No bookings yet</div>}
          {rows.map((r) => (
            <div key={r.id} className={`gantt-label${focus && focus.id === r.id ? ' flash' : ''}`} title={r.hotelName}>
              <span className="gantt-hotel">{r.hotelName}</span>
              <span className="gantt-code">{r.hotelCode}{r.syncProjectId ? ` · project ${r.syncProjectId}` : ''}</span>
            </div>
          ))}
        </div>
        <div className="gantt-scroll" ref={scrollRef}>
          <div className="gantt-grid" style={{ width: days.length * COL }}>
            <div className="gantt-days">
              {days.map((d) => (
                <div key={ymd(d)} className={`gantt-day ${isWeekend(d) ? 'weekend' : ''} ${d < earliest ? 'blocked' : ''} ${d.getUTCDate() === 1 ? 'month-start' : ''}`}
                  style={{ width: COL }} title={fmt(ymd(d))}>
                  {d.getUTCDate() === 1 && <span className="gantt-month">{d.toLocaleDateString(undefined, { month: 'long', timeZone: 'UTC' })}</span>}
                  {d.getUTCDate()}
                </div>
              ))}
            </div>
            {rows.length === 0 && <div className="gantt-row" />}
            {rows.map((r) => (
              <div key={r.id} className="gantt-row">
                {days.map((d) => (
                  <div key={ymd(d)} className={`gantt-cell ${isWeekend(d) ? 'weekend' : ''} ${d < earliest ? 'blocked' : ''} ${d.getUTCDate() === 1 ? 'month-start' : ''}`}
                    style={{ width: COL }} />
                ))}
                {inYear(dateOf(r)) && (
                  <button type="button"
                    className={`gantt-block ${r.status.toLowerCase()}${focus && focus.id === r.id ? ' flash' : ''}`}
                    key={focus && focus.id === r.id ? focus.at : 'block'}
                    style={{ left: offset(parse(dateOf(r))) * COL + 1, width: (data.blockDays || 2) * COL - 2 }}
                    title={`${STATUS_LABEL[r.status]}: ${fmt(dateOf(r))}${r.projectName ? ` — ${r.projectName}` : ''}`}
                    onClick={() => data.canPropose && openForm(r)}>
                    {r.syncProjectId ? '● ' : ''}{STATUS_LABEL[r.status]}
                  </button>
                )}
              </div>
            ))}
            {today.getUTCFullYear() === year && <div className="gantt-today" style={{ left: offset(today) * COL + COL / 2 }} title="Today" />}
          </div>
        </div>
      </div>
      <div className="gantt-legend">
        <span className="gantt-key proposed" /> Proposed
        <span className="gantt-key committed" /> Committed
        <span className="gantt-key declined" /> Declined
        <span className="gantt-key blocked" /> Inside the lead time
        <span>● has its sync project</span>
      </div>

      {/* ── the order form ──────────────────────────────────────────────────────────────────────────────────── */}
      {form && (
        <ConfirmModal
          wide
          formId="sched-order-form"
          closeOnBackdrop={false}
          title={form.requestId ? `Change the booking for ${form.hotelName}` : 'Propose a cutover date'}
          message={`The property, the date, and who in that country can give access. ${fmt(data.earliestDate)} or later.`}
          confirmLabel={saving ? 'Saving…' : form.requestId ? 'Save booking' : 'Propose date'}
          confirmDisabled={saving}
          onCancel={() => !saving && setForm(null)}
        >
        {error && <div className="sched-error">{error}</div>}
        <form id="sched-order-form" className="sched-form" onSubmit={submitForm}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="proposedDate">Cutover date</label>
              <input type="date" id="proposedDate" name="proposedDate" min={data.earliestDate} required
                value={form.proposedDate}
                onChange={(e) => { const v = notEarlier(e.target.value); setForm((f) => ({ ...f, proposedDate: v })); }} />
              {dateNote ? <small className="sched-date-note">{dateNote}</small>
                : <small>Takes {data.blockDays || 2} days. {fmt(data.earliestDate)} or later.</small>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="hotelName">Hotel name</label>
              <input id="hotelName" name="hotelName" required value={form.hotelName} onChange={setField} />
            </div>
            <div className="form-group">
              <label htmlFor="hotelCode">Hotel Code</label>
              <input id="hotelCode" name="hotelCode" required value={form.hotelCode} onChange={setField} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="country">Country</label>
              <CountrySelect name="country" value={form.country} onChange={setField} required />
            </div>
            <div className="form-group">
              <label htmlFor="currency">Currency</label>
              <CurrencySelect id="currency" name="currency" value={form.currency} onChange={setField} required />
            </div>
          </div>
          <fieldset className="sched-contact">
            <legend>Who can give access to the property in that country</legend>
            {(data.contacts || []).length > 0 && (
              <div className="form-group">
                <label htmlFor="savedContact">Saved contact</label>
                <select id="savedContact" value={(data.contacts || []).some((c) => c.contactEmail === form.contactEmail) ? form.contactEmail : ''}
                  onChange={(e) => pickContact(e.target.value)}>
                  <option value="">A new contact, or pick one given before…</option>
                  {[...data.contacts]
                    .sort((a, b) => (b.country === form.country) - (a.country === form.country) || String(a.contactName).localeCompare(String(b.contactName)))
                    .map((c) => (
                      <option key={c.contactEmail} value={c.contactEmail}>
                        {c.contactName}{c.contactRole ? ` — ${c.contactRole}` : ''} ({c.contactEmail}){c.country ? ` · ${c.country}` : ''}
                      </option>
                    ))}
                </select>
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contactName">Name</label>
                <input id="contactName" name="contactName" required value={form.contactName} onChange={setField} />
              </div>
              <div className="form-group">
                <label htmlFor="contactRole">Role</label>
                <input id="contactRole" name="contactRole" value={form.contactRole} onChange={setField} placeholder="e.g. Hotel IT manager" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contactEmail">Email</label>
                <input type="email" id="contactEmail" name="contactEmail" required value={form.contactEmail} onChange={setField} />
              </div>
              <div className="form-group">
                <label htmlFor="contactPhone">Phone</label>
                <input id="contactPhone" name="contactPhone" value={form.contactPhone} onChange={setField} />
              </div>
            </div>
          </fieldset>
          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea id="notes" name="notes" rows={3} value={form.notes} onChange={setField} />
          </div>
        </form>
        </ConfirmModal>
      )}

      {/* ── who is told ── */}
      {data.canPropose && (
        <div className="sched-notify card">
          <button type="button" className="sched-notify-toggle" onClick={() => setNotifOpen((o) => !o)}>
            {notifOpen ? '▾' : '▸'} Notifications
            <span className="sched-reason">
              {' '}— {notif.emails.length} email recipient(s){notif.teamsWebhook ? ', Teams channel set' : ', no Teams channel'}. Every booking change is sent to them.
            </span>
          </button>
          {notifOpen && (
            <NotificationsPanel value={notif} onChange={setNotif} accountId={accountId} persistence={notifPersistence} maxCustom={10} />
          )}
        </div>
      )}

      {/* ── the bookings ────────────────────────────────────────────────────────────────────────────────────── */}
      <h4 className="sched-list-title">Bookings</h4>
      {data.requests.length === 0 ? (
        <p className="sched-sub">No dates proposed yet{data.canPropose ? ' — press Propose a date to book the first property.' : '.'}</p>
      ) : (
        <table className="sched-table">
          <thead>
            <tr><th>Property</th><th>Status</th><th>Date</th><th>Country / currency</th><th>Access contact</th><th>Sync project</th><th /></tr>
          </thead>
          <tbody>
            {data.requests.map((r) => (
              <tr key={focus && focus.id === r.id ? `${r.id}-${focus.at}` : r.id} className={focus && focus.id === r.id ? 'flash' : ''}>
                <td>{r.hotelName}<div className="sched-reason">{r.hotelCode}</div></td>
                <td><span className={`sched-badge ${r.status.toLowerCase()}`}>{STATUS_LABEL[r.status]}</span>
                  {r.declineReason && <div className="sched-reason">{r.declineReason}</div>}
                  <div className="sched-reason">by {r.proposedByEmail || '—'}{r.decidedByEmail ? `; ${r.decidedByEmail}` : ''}</div></td>
                <td>{fmt(dateOf(r))}
                  {r.committedDate && r.committedDate !== r.proposedDate && <div className="sched-reason">asked for {fmt(r.proposedDate)}</div>}</td>
                <td>{r.country} · {r.currency}</td>
                <td>{r.contactName}{r.contactRole ? ` (${r.contactRole})` : ''}<div className="sched-reason">{r.contactEmail}{r.contactPhone ? ` · ${r.contactPhone}` : ''}</div></td>
                <td>{r.syncProjectId
                  ? <a href={`/sync/${r.syncProjectId}`} onClick={(e) => { e.preventDefault(); navigate(`/sync/${r.syncProjectId}`); }}>{r.projectName || `Project ${r.syncProjectId}`}</a>
                  : r.status === 'COMMITTED' && data.canPropose
                    ? <button type="button" className="btn btn-sm btn-primary" onClick={() => createProject(r)}>Create sync project</button>
                    : <span className="sched-reason">—</span>}</td>
                <td className="sched-row-actions">
                  {data.canCommit && r.status === 'PROPOSED' && (
                    <>
                      <button type="button" className="btn btn-sm btn-primary" onClick={() => setDecision({ kind: 'commit', request: r, date: notEarlier(r.proposedDate) })}>Commit</button>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setDateNote(null); setDecision({ kind: 'decline', request: r, reason: '' }); }}>Decline</button>
                    </>
                  )}
                  {data.canPropose && ['PROPOSED', 'COMMITTED', 'DECLINED'].includes(r.status) && (
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => openForm(r)}>Change</button>
                  )}
                  {((r.status === 'COMMITTED' && data.canCommit) || (['PROPOSED', 'DECLINED'].includes(r.status) && data.canPropose)) && (
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setDecision({ kind: 'cancel', request: r })}>Cancel</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {decision && (
        <ConfirmModal
          title={decision.kind === 'commit' ? `Commit to ${decision.request.hotelName}`
            : decision.kind === 'decline' ? 'Decline this date' : 'Cancel this booking'}
          message={decision.kind === 'commit' ? 'The sync project can then be created from this booking, with this date as its cutover.'
            : decision.kind === 'decline' ? 'Say why, so the chain can propose a date that works.'
            : decision.request.syncProjectId ? 'Its sync project is kept as it is.' : 'The booking is withdrawn.'}
          confirmLabel={saving ? 'Saving…' : decision.kind === 'commit' ? 'Commit' : decision.kind === 'decline' ? 'Decline' : 'Cancel booking'}
          cancelLabel="Back"
          danger={decision.kind !== 'commit'}
          onConfirm={decide}
          onCancel={() => setDecision(null)}
        >
          {decision.kind === 'commit' && (
            <div className="form-group">
              <label htmlFor="committedDate">Date</label>
              <input type="date" id="committedDate" min={data.earliestDate} value={decision.date}
                onChange={(e) => { const v = notEarlier(e.target.value); setDecision((d) => ({ ...d, date: v })); }} />
              {dateNote && <small className="sched-date-note">{dateNote}</small>}
            </div>
          )}
          {decision.kind === 'decline' && (
            <div className="form-group">
              <label htmlFor="declineReason">Reason</label>
              <textarea id="declineReason" rows={3} value={decision.reason}
                onChange={(e) => setDecision((d) => ({ ...d, reason: e.target.value }))} />
            </div>
          )}
        </ConfirmModal>
      )}
    </div>
  );
};

export default SchedulingTab;
